import { useState } from "react";
import { Save } from "lucide-react";
import { SaveVizModal } from "./SaveVizModal";
import type { VizSourceRef } from "@/lib/viz/types";

interface Props {
  columnNames: string[];
  rows:        Array<Array<unknown>>;
  /**
   * Lazily capture the rendered chart as a PNG data URL — called at
   * save time so the saved Cognos report embeds the visual, not just
   * the table. Resolves to null when nothing capturable is present
   * (the save then falls back to table-only).
   */
  captureImage: () => Promise<string | null>;
  disabled:    boolean;
  /**
   * B0 (save-viz Feature B): the effective source ref of the data the
   * chart renders. Threaded to the save endpoint so model-backed
   * sources can be saved as LIVE native reports rather than snapshots.
   */
  sourceRef?:  VizSourceRef | null;
  /** The active chart's spec triplet — lets the saver map the archetype. */
  vizSpec?:    { format: string; vizType: string; specJson: string } | null;
}

/**
 * The "Save as Cognos report…" pill button that sits under each
 * rendered viz block. Opens {@link SaveVizModal} on click; the modal
 * owns its own form/network state and dismisses itself on success or
 * cancel.
 *
 * <p>Disabled when {@code disabled} is true (any other follow-up
 * action in flight) or there are no rows to save. We don't gate on
 * admin role here — the backend enforces that and returns 403; the UI
 * surfaces it as a structured error in the modal. Gating client-side
 * too would require fetching the user's role on every page load,
 * which we don't currently do.
 */
export function SaveVizButton({ columnNames, rows, captureImage, disabled, sourceRef, vizSpec }: Props) {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-label="Save as Cognos report"
        title="Persist this chart's data as a Cognos report snapshot"
        className="flex-none inline-flex h-7 items-center gap-1 rounded-full border border-slate-200 bg-white px-3 text-xs text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Save className="h-3 w-3" aria-hidden="true" />
        Save as report…
      </button>
      {open && (
        <SaveVizModal
          columnNames={columnNames}
          rows={rows}
          captureImage={captureImage}
          sourceRef={sourceRef ?? null}
          vizSpec={vizSpec ?? null}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
